import './admin.css';
import { useState, useEffect, useMemo } from 'react';
import { useApiData, } from '../../contexts/apiService';
import NewUserForm from './components/user_form/newUserForm';
import UserGroups from './components/user_groups/userGroups';
import TabToggle from '../../components/common/tab_toggle/tabToggle';
import ApiClientsPanel from './components/api_clients/ApiClientsPanel';
import CommunicationsPage from './components/communications/communicationsPage';
import UserAnalyticsPage from './components/user_analytics/userAnalyticsPage';
import SQLTable, { JsonColumnConfig, TableColumnConfig } from '../../components/sql_table/tableMain';
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';
import { useDataManager } from '../../contexts/dataManager';
import { getUniqueDropdownOptions } from '../../helper_functions';
import { KeyRound, Trash2 } from 'lucide-react';
import { RowActionConfig } from '../../components/sql_table/tableMain';

export interface UserGroup {
    groupId: string;
    groupName: string;
    permissions: {
        viewing: boolean;
        request: boolean;
    };
}

export interface User {
    uid: string;
    id: string;
    email: string;
    name: string;
    company: string;
    viewAs: string;
    hasAcceptedTerms: boolean;
    role: string;
    section: string;
    groups?: UserGroup[];
}

const AdminPage = () => {
    const { allUsers, apiData, fetchAllUsers, handleResetPassword, handleDeleteUser } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    const [dropdownProducers, setDropdownProducers] = useState<{ value: string; label: string }[]>([]);
    const [dropdownEntities, setDropdownEntities] = useState<{ value: string; label: string }[]>([]);
    const [activeTab, setActiveTab] = useState<'Users' | 'Groups' | 'API Clients' | 'Communications' | 'User Analytics'>('Users');
    const { userRole = 'viewer' } = useUserAccess();
    const ADMIN_LEVEL = USER_ROLE_LEVEL['admin'];
    const showTable = Number(USER_ROLE_LEVEL[userRole]) >= ADMIN_LEVEL;
    const users = allUsers;
    
    useEffect(() => {
            if (!hasTableData('producer_definition')) {
                requestTableData('producer_definition');
            }
            if (!hasTableData('producer_groups')) {
                requestTableData('producer_groups');
            }
            if (!hasTableData('entity_definition')) {
                requestTableData('entity_definition');
            }
            if (!hasTableData('entity_producer_history')) {
                requestTableData('entity_producer_history');
            }
    }, []);

    // Fetch users on component mount
    useEffect(() => {
        if (!allUsers.length) {
            fetchAllUsers();
        }
    }, []);

    // getting unique producers
    useEffect(() => {
		if (apiData?.producer_definition) {
            const producerOptions = getUniqueDropdownOptions({
                tableData: apiData?.producer_definition,
                valueColumn: 'producer_name',
                labelColumn: 'producer_name',
            });
            producerOptions.push({ value: 'rockbridge', label: 'rockbridge' });
            setDropdownProducers(producerOptions);
        }
	}, [apiData?.producer_definition]);
    
    const uniqueProducers = useMemo(() => {
        const base = (apiData?.producer_definition || []).map((p: any) => ({
            id: Number(p.id),
            name: String(p.producer_name),
        }));

        // optional dedupe by name
        const seen = new Set<string>();
        return base.filter(p => {
            const key = p.name.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [apiData?.producer_definition]);

    const entityOptions = getUniqueDropdownOptions({
        tableData:apiData?.entity_definition,
        valueColumn: 'id',
        labelColumn: 'entity_name',
        extraFields: ['current_producer_id']
    });
    
    const producerOptionsID = getUniqueDropdownOptions({
        tableData: apiData?.producer_definition,
        valueColumn: 'id',
        labelColumn: 'producer_name',
    });

    const userRowActions: RowActionConfig[] = [
        {
            id: 'reset_password',
            label: 'Reset Password',
            variant: 'success',
            icon: <KeyRound size={14} />,
            onClick: async (rowData: any) => {
                await handleResetPassword(rowData.uid, rowData.email);
                window.alert(`Reset password email sent to ${rowData.email}`);
            },
            isDisabled: (rowData: any) => !rowData?.uid || !rowData?.email,
            requiresConfirmation: true,
            confirmationConfig: {
                title: 'Reset Password',
                message: (rowData: any) => `Reset password for user ${rowData?.email || rowData?.name || ''}?`,
                requiresReason: false,
            },
        },
        {
            id: 'delete_user',
            label: 'Delete',
            variant: 'danger',
            icon: <Trash2 size={14} />,
            isDisabled: (rowData: any) => !rowData?.uid,
            onClick: async (rowData: any) => {
                await handleDeleteUser(rowData.uid);
                await fetchAllUsers();
            },
            requiresConfirmation: true,
            confirmationConfig: {
                title: 'Delete User',
                message: (rowData: any) => `Delete user ${rowData?.email || rowData?.name || ''}?`,
                requiresReason: false,
            },
        },
    ];

    const handleUserCreated = () => {
        fetchAllUsers();
    };

    if (!showTable) {
        return <p>Not Allowed</p>;
    }
    
    const tableCols: TableColumnConfig[] = [
        { colName: 'name', colHeader: 'Name', colType: 'text', editable: true },
        { colName: 'email', colHeader: 'Email', colType: 'text', editable: true },
        { colName: 'company', colHeader: 'Company', colType: 'text', editable: true, inputType: 'dropdown', dropdownOptions: dropdownProducers },
        { colName: 'producer_id', colHeader: 'Company (producer_id)', colType: 'number', editable: true, inputType: 'dropdown', dropdownOptions: producerOptionsID },
        { colName: 'dataAccess', colHeader: 'Data Access', colType: 'text', enableSidepanel: true },
        { colName: 'role', colHeader: 'Role', colType: 'text', editable: true},
        { colName: 'hasAcceptedTerms', colHeader: 'Has Accepted T&C', colType: 'text', editable: true },
    ];
    
    return (
        <div className="admin-mainContainer">
            
            <div className="admin-tabContainer">
                <TabToggle
                    labels={['Users', 'Groups', 'API Clients', 'Communications', 'User Analytics']}
                    activeTab={activeTab}
                    onChange={(label) => setActiveTab(label as 'Users' | 'Groups' | 'API Clients' | 'Communications' | 'User Analytics')}
                />
            </div>

            {activeTab === 'Groups' && (
                <UserGroups />
            )}

            {activeTab === 'API Clients' && (
                <ApiClientsPanel />
            )}

            {activeTab === 'Communications' && (
                <CommunicationsPage />
            )}

            {activeTab === 'User Analytics' && (
                <UserAnalyticsPage />
            )}
            
            <div>
                {activeTab === 'Users' && (
                    <>
                        <div className="admin-formContainer">
                            <NewUserForm 
                                uniqueProducers={uniqueProducers}
                                users={users}
                                onUserCreated={handleUserCreated}
                            />
                        </div>

                        <div className="admin-userTableContainer">
                            <SQLTable
                                tableType={'firebase'}
                                skipDataFetch={true}
                                tableName={'users'}
                                tableData={users}
                                idColumn={'uid'}
                                columnConfigs={tableCols}
                                rowActions={userRowActions}
                                rowActionsPosition="end"
                                rowActionsWidth={220}
                                showHeader={true}
                                showTable={true}
                                selectableRows={true}
                                refreshable={false}
                                enableFilters={true}
                                enableSummary={true}
                                summaryConfig={[]}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminPage;